import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Package, MapPin, Clock, ChevronRight, RefreshCw } from "lucide-react";
import axios from "axios";
import { getBackendUrl } from "../utils/api";

const API_URL = getBackendUrl();

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-700",
  payment_done: "bg-blue-100 text-blue-700",
  rider_assigned: "bg-purple-100 text-purple-700",
  picked_up: "bg-indigo-100 text-indigo-700",
  in_transit: "bg-cyan-100 text-cyan-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
  refunded: "bg-gray-100 text-gray-700"
};

const FleetyGoOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userData = localStorage.getItem("fleety_user");
    if (!userData) {
      navigate("/login");
      return;
    }
    const user = JSON.parse(userData);
    fetchOrders(user.id);
  }, [navigate]);

  const fetchOrders = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/fleetygo/orders/user/${userId}`);
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
    setLoading(false);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const handleRepeatDelivery = (order, e) => {
    e.stopPropagation(); // Prevent navigation to tracking page
    
    // Pre-fill booking data from the previous order
    const pickupData = {
      name: order.sender_name || "",
      phone: order.sender_phone || "",
      address: order.sender_address || "",
      address_line2: order.sender_address_line2 || "",
      lat: order.sender_lat || null,
      lng: order.sender_lng || null
    };
    
    const dropData = {
      name: order.receiver_name || "",
      phone: order.receiver_phone || "",
      address: order.receiver_address || "",
      address_line2: order.receiver_address_line2 || "",
      lat: order.receiver_lat || null,
      lng: order.receiver_lng || null
    };
    
    const packageData = {
      weight_category: order.weight_category || "light",
      contents: order.package_contents || "",
      description: order.package_description || ""
    };
    
    // Store in sessionStorage for the booking page
    sessionStorage.setItem("fleetygo_pickup", JSON.stringify(pickupData));
    sessionStorage.setItem("fleetygo_drop", JSON.stringify(dropData));
    sessionStorage.setItem("fleetygo_package", JSON.stringify(packageData));
    sessionStorage.setItem("fleetygo_step", "4"); // Go to Review step
    
    // Navigate to booking page
    navigate("/fleetygo/book");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="fleetygo-orders">
      <header className="bg-[#3FA9F5] p-4 flex items-center gap-4 sticky top-0 z-10">
        <button onClick={() => navigate("/fleetygo")} className="text-white">
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-white text-lg font-semibold">Past Deliveries</h1>
      </header>

      <main className="flex-1 p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-[#3FA9F5] border-t-transparent rounded-full" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <Package size={48} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No deliveries yet</p>
            <button
              onClick={() => navigate("/fleetygo/book")}
              className="mt-4 text-[#3FA9F5] font-medium"
            >
              Send your first package →
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => (
              <div
                key={order.id}
                className="w-full bg-white rounded-xl p-4 shadow-sm"
              >
                <button
                  onClick={() => navigate(`/fleetygo/track/${order.id}`)}
                  className="w-full text-left"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${STATUS_COLORS[order.status]}`}>
                        {order.status.replace("_", " ").toUpperCase()}
                      </span>
                      <p className="text-xs text-gray-400 mt-1">#{order.order_number || "N/A"}</p>
                    </div>
                    <p className="font-bold text-[#3FA9F5]">₹{order.total}</p>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <span className="truncate">{order.sender_address?.split(",")[0] || "Pickup"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full" />
                    <span className="truncate">{order.receiver_address?.split(",")[0] || "Drop"}</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {formatDate(order.created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      {order.distance_km} km
                      <ChevronRight size={14} />
                    </span>
                  </div>
                </button>
                
                {/* Repeat Delivery Button - Show for delivered orders */}
                {order.status === "delivered" && (
                  <button
                    onClick={(e) => handleRepeatDelivery(order, e)}
                    className="w-full mt-3 py-2 px-4 bg-[#3FA9F5]/10 hover:bg-[#3FA9F5]/20 text-[#3FA9F5] rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors"
                  >
                    <RefreshCw size={16} />
                    Repeat Delivery
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default FleetyGoOrdersPage;
